import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faImage, faSignature, faMapMarkerAlt,
  faPhone, faEnvelope, faUniversity, faIdCard,
  faCheckCircle, faExclamationTriangle, faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';

// ============================================================
// 1. CACHE DEXIE (24h) avec fallback
// ============================================================
const db = new Dexie('EntrepriseCacheDB');
db.version(1).stores({ entreprise: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedEntreprise(key, allowExpired = false) {
  try {
    const entry = await db.entreprise.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}

async function setCachedEntreprise(key, data) {
  await db.entreprise.put({ key, data, timestamp: Date.now() });
}

// ============================================================
// 2. FETCH AVEC RETRY (gestion 429)
// ============================================================
const fetchWithRetry = async (url, options = {}, retries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.status === 429 && attempt < retries) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter
          ? parseInt(retryAfter, 10) * 1000
          : baseDelay * Math.pow(2, attempt);
        console.warn(`⏳ 429 détecté, nouvelle tentative dans ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      const text = await response.text();
      if (!response.ok) throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
      try {
        return JSON.parse(text);
      } catch (parseError) {
        throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
      }
    } catch (error) {
      if (attempt === retries) throw error;
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// ============================================================
// 3. CONFIGURATION API
// ============================================================
const API_URL = `${API_CONFIG2.apiUrl}entreprise/api_entreprise.php`;

// ============================================================
// 4. COMPOSANT PRINCIPAL
// ============================================================
const CompanyProfile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const isFetchingRef = useRef(false);   // sémaphore
  const toastTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    nom: '',
    logoUrl: '',
    signatureUrl: '',
    adresse: '',
    telephone: '',
    email: '',
    compteBancaire: '',
    rccm: '',
    idnat: '',
    nimpot: ''
  });

  // ============================================================
  // 5. GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((msg, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ msg, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // 6. CHARGEMENT DES DONNÉES (avec cache, retry, sémaphore)
  // ============================================================
  const fetchEntreprise = useCallback(async (forceRefresh = false) => {
    // Éviter les appels concurrents
    if (!forceRefresh && isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);
    const cacheKey = 'entreprise';

    try {
      // 1. Cache si non forcé
      if (!forceRefresh) {
        const cached = await getCachedEntreprise(cacheKey);
        if (cached) {
          setData(cached);
          setFormData(cached);
          setLoading(false);
          isFetchingRef.current = false;
          return;
        }
      }

      // 2. Requête réseau (avec retry)
      const data = await fetchWithRetry(`${API_URL}?action=fetch`);

      if (data.status === 'success') {
        const entrepriseData = data.data || {};
        setData(entrepriseData);
        setFormData(entrepriseData);
        await setCachedEntreprise(cacheKey, entrepriseData);
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      // Tentative de récupération depuis le cache même périmé
      const stale = await getCachedEntreprise(cacheKey, true);
      if (stale) {
        setData(stale);
        setFormData(stale);
        showToast('Données chargées depuis le cache (peut être obsolète)', 'warning');
      } else {
        showToast(err.message || 'Erreur de chargement', 'danger');
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [showToast]);

  useEffect(() => {
    fetchEntreprise();
  }, [fetchEntreprise]);

  // ============================================================
  // 7. GESTION DU FORMULAIRE
  // ============================================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const json = await fetchWithRetry(`${API_URL}?action=update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (json.status === 'success') {
        showToast(json.message, 'success');
        setData(json.data);
        await setCachedEntreprise('entreprise', json.data);
      } else {
        showToast(json.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur serveur', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefresh = () => {
    if (!loading) fetchEntreprise(true);
  };

  // ============================================================
  // 8. AFFICHAGE : CHARGEMENT / ERREUR
  // ============================================================
  if (loading && !data) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des informations...</p>
      </div>
    );
  }

  // ============================================================
  // 9. RENDU PRINCIPAL
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – COMPANY PROFILE
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0a0e27;
          --card-bg: rgba(13, 17, 38, 0.65);
          --card-border: rgba(79, 156, 245, 0.18);
          --text-primary: #e0e7ff;
          --text-secondary: #8892b0;
          --text-muted: #64748b;
          --radius: 24px;
        }

        .company-profile-wrapper {
          padding: 30px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        /* Effet de fond bleu subtil */
        .company-profile-wrapper::before {
          content: '';
          position: absolute;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%);
          top: -150px;
          left: -100px;
          border-radius: 50%;
          z-index: 0;
        }

        .glass-card {
          position: relative;
          z-index: 1;
          background: var(--card-bg);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 2.5rem;
          box-shadow: 0 20px 60px rgba(0, 20, 50, 0.8), inset 0 0 20px rgba(79,156,245,0.08);
          max-width: 900px;
          width: 100%;
          margin: 0 auto;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        @media (prefers-reduced-motion: no-preference) {
          .glass-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 30px 80px rgba(0, 40, 100, 0.9), 0 0 30px var(--accent-blue-glow);
          }
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 2rem;
          border-bottom: 1px solid rgba(79, 156, 245, 0.1);
          padding-bottom: 1rem;
        }
        .section-title h2 {
          font-weight: 700;
          background: linear-gradient(135deg, #a5c9ff, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
          font-size: 1.8rem;
        }

        .monetra-input {
          background: rgba(16, 23, 55, 0.5);
          border: 1px solid rgba(79, 156, 245, 0.15);
          border-radius: 14px;
          color: #e0e7ff;
          padding: 12px 16px;
          width: 100%;
          transition: all 0.3s ease;
          font-size: 0.95rem;
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(25, 35, 80, 0.7);
          box-shadow: 0 0 0 4px rgba(79, 156, 245, 0.15);
        }
        .monetra-input::placeholder {
          color: rgba(160, 180, 220, 0.4);
        }

        .monetra-btn {
          padding: 12px 28px;
          border-radius: 30px;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: #fff;
          border: none;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
          box-shadow: 0 6px 20px var(--accent-blue-glow);
          display: inline-flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }
        .monetra-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.4);
          background: linear-gradient(135deg, #4f9cf5, #3b82f6);
        }
        .monetra-btn:disabled {
          background: #1f2a48;
          color: #556;
          box-shadow: none;
        }

        .monetra-btn.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.15);
          color: var(--text-primary);
          box-shadow: none;
        }
        .monetra-btn.outline:hover:not(:disabled) {
          background: rgba(255,255,255,0.05);
          border-color: rgba(255,255,255,0.25);
          transform: translateY(-2px);
        }

        .field-icon {
          color: var(--accent-blue);
          font-size: 1rem;
        }

        .toast-custom {
          background: rgba(10, 14, 39, 0.9) !important;
          backdrop-filter: blur(20px);
          border: 1px solid rgba(79, 156, 245, 0.3) !important;
          border-radius: 16px !important;
          color: #e0e7ff !important;
          box-shadow: 0 20px 50px rgba(0, 0, 50, 0.7) !important;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 12px;
          animation: slideIn 0.35s ease;
          position: relative;
          overflow: hidden;
        }
        .toast-custom.success {
          border-left: 4px solid var(--accent-blue);
        }
        .toast-custom.danger {
          border-left: 4px solid #ef4444;
        }
        .toast-custom.warning {
          border-left: 4px solid #f59e0b;
        }
        .toast-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: currentColor;
          animation: progressShrink 5s linear forwards;
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes progressShrink {
          from { width: 100%; }
          to { width: 0%; }
        }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          margin-left: auto;
          padding: 0 4px;
          transition: color 0.2s;
        }
        .btn-close-custom:hover {
          color: var(--text-primary);
        }

        /* ============ RESPONSIVE MOBILE ============ */
        @media (max-width: 768px) {
          .company-profile-wrapper {
            padding: 15px;
            align-items: flex-start;
          }
          .glass-card {
            padding: 1.5rem;
            border-radius: 20px;
          }
          .section-title h2 {
            font-size: 1.4rem;
          }
          .monetra-input {
            font-size: 14px;
            padding: 10px 14px;
          }
          .monetra-btn {
            font-size: 0.9rem;
            padding: 10px 20px;
          }
          /* Tous les champs en pleine largeur */
          .row > [class*="col-"] {
            width: 100%;
            max-width: 100%;
            flex: 0 0 100%;
          }
        }
        @media (max-width: 576px) {
          .glass-card {
            padding: 1rem;
          }
          .section-title h2 {
            font-size: 1.2rem;
          }
          .monetra-btn {
            font-size: 0.85rem;
            padding: 8px 16px;
          }
        }
      `}</style>

      <div className="company-profile-wrapper">
        <div className="glass-card">
          <div className="section-title">
            <FontAwesomeIcon icon={faBuilding} style={{ color: 'var(--accent-blue)', fontSize: '1.8rem' }} />
            <h2>Profil de l'entreprise</h2>
          </div>

          <Form onSubmit={handleSubmit}>
            <div className="row g-3">
              {/* Nom */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faBuilding} className="me-2 field-icon" />
                    Nom de l'entreprise
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="nom"
                    value={formData.nom || ''}
                    onChange={handleChange}
                    required
                    className="monetra-input text-white"
                    placeholder="Ex: Monetra SAS"
                  />
                </Form.Group>
              </div>

              {/* Logo URL */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faImage} className="me-2 field-icon" />
                    URL du logo
                  </Form.Label>
                  <Form.Control
                    type="url"
                    name="logoUrl"
                    value={formData.logoUrl || ''}
                    onChange={handleChange}
                    className="monetra-input text-white"
                    placeholder="https://example.com/logo.png"
                  />
                </Form.Group>
              </div>

              {/* Signature URL */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faSignature} className="me-2 field-icon" />
                    URL de la signature
                  </Form.Label>
                  <Form.Control
                    type="url"
                    name="signatureUrl"
                    value={formData.signatureUrl || ''}
                    onChange={handleChange}
                    className="monetra-input text-white"
                    placeholder="https://example.com/signature.png"
                  />
                </Form.Group>
              </div>

              {/* Adresse */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 field-icon" />
                    Adresse
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="adresse"
                    value={formData.adresse || ''}
                    onChange={handleChange}
                    className="monetra-input text-white"
                    placeholder="Adresse complète"
                    rows={2}
                  />
                </Form.Group>
              </div>

              {/* Téléphone */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faPhone} className="me-2 field-icon" />
                    Téléphone
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="telephone"
                    value={formData.telephone || ''}
                    onChange={handleChange}
                    className="monetra-input text-white"
                    placeholder="+33 1 23 45 67 89"
                  />
                </Form.Group>
              </div>

              {/* Email */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2 field-icon" />
                    Email
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    className="monetra-input text-white"
                    placeholder="contact@entreprise.com"
                  />
                </Form.Group>
              </div>

              {/* Compte bancaire */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faUniversity} className="me-2 field-icon" />
                    Compte bancaire
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="compteBancaire"
                    value={formData.compteBancaire || ''}
                    onChange={handleChange}
                    className="monetra-input text-white"
                    placeholder="FR76 3000 3001 0002 0003 45"
                  />
                </Form.Group>
              </div>

              {/* RCCM */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faIdCard} className="me-2 field-icon" />
                    RCCM
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="rccm"
                    value={formData.rccm || ''}
                    onChange={handleChange}
                    required
                    className="monetra-input text-white"
                    placeholder="Numéro RCCM"
                  />
                </Form.Group>
              </div>

              {/* ID National */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faIdCard} className="me-2 field-icon" />
                    ID National
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="idnat"
                    value={formData.idnat || ''}
                    onChange={handleChange}
                    required
                    className="monetra-input text-white"
                    placeholder="Numéro d'identification nationale"
                  />
                </Form.Group>
              </div>

              {/* N° Impôt */}
              <div className="col-md-6 col-12">
                <Form.Group>
                  <Form.Label className="text-secondary small">
                    <FontAwesomeIcon icon={faIdCard} className="me-2 field-icon" />
                    N° Impôt
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="nimpot"
                    value={formData.nimpot || ''}
                    onChange={handleChange}
                    required
                    className="monetra-input text-white"
                    placeholder="Numéro d'impôt"
                  />
                </Form.Group>
              </div>
            </div>

            {/* Boutons */}
            <div className="d-flex gap-3 mt-4 flex-wrap">
              <button
                type="submit"
                className="monetra-btn"
                disabled={submitting}
              >
                {submitting ? (
                  <><Spinner as="span" animation="border" size="sm" className="me-2" /> Enregistrement...</>
                ) : (
                  'Enregistrer les modifications'
                )}
              </button>
              <button
                type="button"
                className="monetra-btn outline"
                onClick={handleRefresh}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                Rafraîchir
              </button>
            </div>
          </Form>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, minWidth: '300px' }}>
          <div className={`toast-custom ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : toast.variant === 'danger' ? faExclamationTriangle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : toast.variant === 'danger' ? 'text-danger' : 'text-warning'}
            />
            <span>{toast.msg}</span>
            <button className="btn-close-custom" onClick={() => setToast(null)}>✕</button>
            <div className="toast-progress" style={{ color: toast.variant === 'success' ? 'var(--accent-blue)' : '#ef4444' }} />
          </div>
        </div>
      )}
    </>
  );
};

export default CompanyProfile;